import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Box, Grid, IconButton, Menu } from '@material-ui/core';
import { makeStyles, lighten } from '@material-ui/core/styles';
import { formatDate, LoanStatus } from '@roc/feature-utils';
import { DataGrid, GridColumnProps, LinkColumn, SelectFilterComponent, SelectFloatingFilterComponent, SelectMode, SelectSearchFloatingFilterComponent, SelectSide, StatusChip, StatusType } from '@roc/ui';
import { formatCurrency, formatPercentage } from '@roc/ui/utils';
import { observer } from 'mobx-react';
import { StatusDropdownGridEditor } from './receivables/statusDropdownGridEditor';
import { MoreVert } from '@material-ui/icons';
import { StatusEditorReceivablesBills } from './receivablesBills/statusEditorReceivablesBills';
import { StatusEditorPayables } from './payables/statusEditorPayables';
import { CommentEditor } from './receivablesBills/commentEditorReceivableBills';
import { MultipleSearchFilter, MultipleSearchFloatingFilter } from './filters/multiSelectFilter';
import { PayableReceivablesTypes } from '../types/types';
import { AdjustmentEditor } from './payables/adjustmentEditor';

const useStyles = makeStyles(theme => ({
  gridContainer: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    height: '80vh',
    overflow: 'hidden',
  },
  warningRow: {
    '& .ag-cell': {
      backgroundColor: lighten(theme.palette.warning.main, 0.85)
    }
  },
}));

interface ProtectiveAdvancesGridInterface {
  counterPartyExpenseStore: any;
  columns: GridColumnProps[];
  selectModeEnabled: boolean;
  toolbar?: ReactNode;
  actions: (record, handleMenuClose) => ReactNode;
  loanId?: any;
}

export const ProtectiveAdvancesGrid = observer(({ counterPartyExpenseStore, columns, selectModeEnabled, toolbar, actions, loanId }: ProtectiveAdvancesGridInterface) => {
  const classes = useStyles();
  const { tab, counterPartyExpenseGridStore } = counterPartyExpenseStore;
  const [anchorEl, setAnchorEl] = useState<HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [record, setRecord] = useState<any>({});
  const [gridApi, setGridApi] = useState(null);

  useEffect(() => {
    onTabChange();
  }, [tab]);

  useEffect(() => {
    gridApi?.deselectAll();
    counterPartyExpenseStore.resetToolbarInfo();
  }, [counterPartyExpenseStore.entity])

  const onGridReady = params => {
    setGridApi(params.api);
  };

  const onTabChange = () => {
    counterPartyExpenseStore.setEntity(loanId || '');
    counterPartyExpenseGridStore.resetAndFetchGridData();
    counterPartyExpenseStore.resetToolbarInfo();
  };

  const frameworkComponents = {
    dateCellRenderer: ({ value }) => {
      return value ? formatDate(value, 'MM/dd/yyyy') : '';
    },
    currencyCellRenderer({ value }) {
      return formatCurrency(value);
    },
    percentageCellRenderer({ value }) {
      return formatPercentage(value);
    },
    booleanCellRenderer({ value }) {
      return value ? 'Yes' : 'No';
    },
    actionCellRenderer: params => {
      return (
        <>
          <IconButton onClick={e => {
            openMenu(e, params.node.data)
          }}>
            <MoreVert color="primary" />
          </IconButton>
        </>
      );
    },
    dropdownGridEditor: StatusDropdownGridEditor,
    statusReceivablesBillsEditor: StatusEditorReceivablesBills,
    statusPayablesEditor: StatusEditorPayables,
    commentEditor: CommentEditor,
    adjustmenEditor: AdjustmentEditor,
    statusCellRenderer(params) {
      return (
        !!params.value && (
          <StatusChip
            statusType={StatusType.PROTECTIVE_ADVANCES}
            label={params.value}
            size="small"
          />
        )
      );
    },
    bankInfoCellRenderer(params) {
      return (
        !!params.value && (
          <StatusChip
            statusType={StatusType.VENDOR_BANK_INFO}
            label={params.value}
            size="small"
          />
        )
      );
    },
    loanStatusCellRenderer(params) {
      return (
        !!params.value && (
          <StatusChip
            statusType={StatusType.LOAN}
            label={params.value}
            size="small"
          />
        )
      );
    },
    selectFloatingFilter: SelectFloatingFilterComponent,
    selectSearchFloatingFilter: SelectSearchFloatingFilterComponent,
    selectFilter: SelectFilterComponent,
    multipleSearchFilter: MultipleSearchFilter,
    multipleSearchFloatingFilter: MultipleSearchFloatingFilter,
    linkColumnCellRenderer(params) {
      const row = params.node.data;
      return (
        row?.status === PayableReceivablesTypes.REJECTED ? (<span> {params.value} </span>) : (
          <LinkColumn
            value={params.value}
            url={'#'}
            onClick={() => {
              counterPartyExpenseStore.openModal(row);
            }}
          />
        )
      );
    },
    linkCurrencyCellRenderer(params) {
      const row = params.node.data;
      return (
        (
          <LinkColumn
            value={formatCurrency(params.value)}
            url={'#'}
            onClick={() => {
              counterPartyExpenseStore.openHistoryAdjustmentModal(row);
            }}
          />
        )
      );
    },
  };

  const onSortChanged = params => {
    const sortModel = params.api.getSortModel();
    counterPartyExpenseGridStore.setSortModel(sortModel);
  };

  const onFilterChanged = params => {
    const filterModel = params.api.getFilterModel();
    counterPartyExpenseGridStore.setFilterModel(filterModel);
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>, record) => {
    setRecord(record);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const onRowSelected = (params) => {
    // TODO - WE SHOULD USE A TRANSACTION ID
    counterPartyExpenseStore.setRecordsId(params?.data?.recordId, params?.node.selected);
    counterPartyExpenseStore.setTotalAmount(params?.data?.amount, params?.node.selected);
  };

  const handleModelUpdated = (params) => {
    params.api.forEachNode((node) => {
      if (counterPartyExpenseStore.recordsIds.includes(node.data?.recordId)) {
        node.setSelected(true);
      }
    });
  }

  const rowClassRules = useMemo(
    () => ({
      [classes.warningRow]: (params: any) =>
        (params.data?.status === PayableReceivablesTypes.PENDING || params.data?.status === PayableReceivablesTypes.APPROVED)
        && (params.data?.loanStatus === LoanStatus.PAID_OFF || params.data?.loanStatus === LoanStatus.PAID_OFF_BUYOUT),
    }),
    [classes.warningRow]
  );



  return (
    <Box>
      <Grid item className={classes.gridContainer}>
        <DataGrid
          columns={columns}
          rows={counterPartyExpenseGridStore.gridData.data.rows}
          frameworkComponents={frameworkComponents}
          selectMode={selectModeEnabled && SelectMode.MULTIPLE}
          selectSide={SelectSide.LEFT}
          checkBoxColumnPinned={true}
          headerCheckboxSelection={true}
          suppressRowClickSelection={true}
          toolbar={toolbar}
          key={tab}
          onSortChanged={onSortChanged}
          onFilterChanged={onFilterChanged}
          isLoading={counterPartyExpenseGridStore.isLoading}
          showFilters={counterPartyExpenseGridStore.showFilters}
          sortModel={counterPartyExpenseGridStore.sortModel}
          filterModel={counterPartyExpenseGridStore.filterModel}
          paginationData={counterPartyExpenseGridStore.gridData.meta}
          setPageNumber={counterPartyExpenseGridStore.setPageNumber}
          onRowSelected={onRowSelected}
          gridReady={onGridReady}
          getRowNodeId={(data) => data.recordId}
          handleModelUpdated={handleModelUpdated}
          rowClassRules={rowClassRules}
        />
      </Grid>
      <Menu
        id="menu-appbar"
        getContentAnchorEl={null}
        anchorEl={anchorEl}
        keepMounted
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        open={menuOpen}
        onClose={handleMenuClose}
      >
        {actions(record, handleMenuClose)}
      </Menu>
    </Box>
  );
});
